import type { ReactNode } from 'react';
import { DisplayTitle } from '@/components/ui/text';
import { Tile } from '@/components/ui/tile';
import { NewStoryHero } from '@/components/dashboard/new-story-hero';

export interface DashboardHeroAction {
  label: string;
  glyph: ReactNode;
  onClick: () => void;
}

interface DashboardHeroProps {
  /** The name the welcome greets. */
  name: string;
  onNewStory: () => void;
  isCreating?: boolean;
  /** Why New Story can't be used here (a phone), shown in its place. */
  blockedLabel?: string;
  /** The utility tiles at the right: Download, Demo, Discord, Feedback, Tutorials, Tour. */
  actions: readonly DashboardHeroAction[];
  /** Where the hero's content sits: the page's container on the dashboard, or a padded box in a preview. */
  className?: string;
}

/** The dashboard's hero band: the welcome, New Story, and the utility tiles. The dashboard and the design system's preview both draw it. */
export function DashboardHero({ name, onNewStory, isCreating, blockedLabel, actions, className }: DashboardHeroProps) {
  return (
    <div className="relative hero-band">
      {/* Background pattern, faded with the set's sheen */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 opacity-lureo-sheen bg-[radial-gradient(circle_at_50%_50%,color-mix(in_oklab,var(--lureo-mint)_14%,transparent)_0%,transparent_50%)]" />
        <div className="absolute inset-0 opacity-lureo-sheen bg-[linear-gradient(45deg,transparent_25%,color-mix(in_oklab,var(--lureo-secondary)_5%,transparent)_25%,color-mix(in_oklab,var(--lureo-secondary)_5%,transparent)_50%,transparent_50%,transparent_75%,color-mix(in_oklab,var(--lureo-secondary)_5%,transparent)_75%)] bg-[length:20px_20px]" />
      </div>

      <div className={className ?? 'container relative pb-8 pt-16 md:pb-10 md:pt-20'}>
        <div className="flex flex-col items-center justify-between gap-8 lg:flex-row lg:items-end">
          <div className="flex-1 text-center lg:text-left">
            <DisplayTitle className="mb-4">
              Welcome, <span className="text-lureo-mint">{name}</span>
            </DisplayTitle>
            <p className="mb-6 max-w-2xl text-lureo-18 text-lureo-on-emphasis-subtle md:text-lureo-20 leading-7 md:leading-7">
              Create, manage, and publish immersive location-based AR storytelling experiences
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row lg:justify-start">
              <NewStoryHero onClick={onNewStory} isLoading={isCreating} blockedLabel={blockedLabel} className="lg:max-w-sm" />
            </div>
          </div>

          <div className="flex flex-1 justify-center lg:justify-end">
            <div data-tour="hero-ctas" className="grid w-full max-w-md grid-cols-3 gap-3 sm:gap-4">
              {actions.map(({ label, glyph, onClick }) => (
                <Tile key={label} tone="emphasis" onClick={onClick} glyph={glyph} label={label} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
